import { Button } from 'src/design-system'
import { flexRender } from '@tanstack/react-table'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { MinusCircle, Plus } from '@phosphor-icons/react'
import { observer } from 'mobx-react-lite'
import * as React from 'react'
import { SearchFilter } from 'components/search-filter'
import { trackEvent } from 'app/packs/src/services/event-tracker'
import { useTable } from './context/table'
import * as Table from 'components/table'
import { OverviewCards, SkillFilter } from '../overview-cards'
import * as SecondaryNav from 'components/secondary-nav'

type UserPersonalSkillsTableProps = {
  onAddSkillsClick?: () => void
}

export const UserPersonalSkillsTable = observer(
  ({ onAddSkillsClick }: UserPersonalSkillsTableProps) => {
    const {
      columnFilters,
      data,
      globalFilter,
      setGlobalFilter,
      table,
      loading,
    } = useTable()

    const source = 'user-personal-skills-table'

    const [selectedFilter, setSelectedFilter] =
      React.useState<SkillFilter>('all')

    const onClearFilters = () => {
      table.resetColumnFilters()
      table.resetGlobalFilter()
      table.resetSorting()
      setSelectedFilter('all')
    }

    const onFilterClick = (filter: SkillFilter) => {
      setSelectedFilter(filter)

      // Clear existing filters
      table.getColumn('skillType')?.setFilterValue(undefined)
      table.getColumn('source')?.setFilterValue(undefined)

      if (filter === 'hard' || filter === 'soft') {
        table.getColumn('skillType')?.setFilterValue(filter)
      } else if (filter === 'declared') {
        // Declared skills are 'assigned' or 'self_declared'
        table.setColumnFilters([
          {
            id: 'source',
            value: ['assigned', 'self_declared'],
          },
        ])
      } else if (filter === 'inferred') {
        table.getColumn('source')?.setFilterValue('implied')
      }

      trackEvent('$track_user_personal_skills_filter', { filter })
    }

    const showClearFiltersButton = React.useMemo(() => {
      return columnFilters.length > 0 || (globalFilter && globalFilter !== '')
    }, [columnFilters, globalFilter])

    const counts = React.useMemo(() => {
      const all = data.length
      const hard = data.filter((d) => d.skillType === 'hard').length
      const soft = data.filter((d) => d.skillType === 'soft').length
      const declared = data.filter(
        (d) => d.source === 'assigned' || d.source === 'self_declared'
      ).length
      const inferred = data.filter((d) => d.source === 'implied').length
      return { all, hard, soft, declared, inferred }
    }, [data])

    return (
      <div>
        <div className="px-4 pt-4 pb-2 flex justify-between items-center">
          <SecondaryNav.Header>Personal Skills</SecondaryNav.Header>
          {onAddSkillsClick && (
            <Button
              onClick={onAddSkillsClick}
              variant="default"
              className="bg-[#1D264A] hover:bg-[#161d38]"
            >
              Add Skills
            </Button>
          )}
        </div>
        <OverviewCards
          counts={counts}
          selectedFilter={selectedFilter}
          onFilterClick={onFilterClick}
        />
        <div className="pt-4">
          <div className="flex flex-wrap gap-2 mb-5 px-4">
            <SearchFilter
              name="user-personal-skills-search"
              onChange={(value) => {
                setGlobalFilter(String(value))
              }}
              value={globalFilter ?? ''}
            />
            {showClearFiltersButton && (
              <Button
                className="h-[34px]"
                onClick={onClearFilters}
                variant="outline"
              >
                <MinusCircle
                  aria-hidden
                  className="h-4 text-theme-40 w-4"
                  weight="bold"
                />
                Clear filters
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <Table.Root>
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Row key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Table.Header
                        key={header.id}
                        header={header}
                        loading={loading}
                        source={source}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Table.Header>
                    ))}
                  </Table.Row>
                ))}
              </Table.Head>

              <Table.Body>
                {table.getRowModel().rows.length === 0 && (
                  <Table.NoResultsRow colspan={table.getAllColumns().length} />
                )}

                {table.getRowModel().rows.map((row, index) => {
                  const cells = row.getVisibleCells()

                  return (
                    <Table.Row
                      showBottomBorder={
                        index < table.getRowModel().rows.length - 1
                      }
                      key={row.id}
                    >
                      {cells.map((cell) => (
                        <Table.Cell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
            <div className="px-3 flex justify-center p-[60px]">
              <Table.Pagination table={table} />
            </div>
          </div>
        </div>
      </div>
    )
  }
)
